import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IconX } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { Button, Text } from "@fragments";

import PostInfo from "./components/PostInfo";
import Comments from "./components/Comments";

import "./styles.scss";

// Desktop geometry (mirrors styles.scss): the column height, the side
// column width, the gap between surfaces, the viewport share the layout may
// take and the photo's own cap.
const MAX_H = 0.88;
const MAX_H_PX = 860;
const MAX_W = 0.96;
const SIDE_WIDTH = 420;
const GAP = 24;
const MEDIA_MAX_WIDTH = 980;
const STACK_BELOW = 900;

/**
 * PostModalView — a bespoke overlay (not the Modal fragment) made of three
 * separate surfaces: the photo (01), the publisher / text / actions panel
 * (02.A) and the comment thread (02.B). On desktop the column height is fixed
 * and the photo is sized in px to that height and its own ratio, so all three
 * surfaces always share one height; under 900px everything stacks.
 */
export default function PostModalView({
    open,
    zIndex,
    post,
    notFound,
    loading,
    comments,
    me,
    isAdmin,
    sending,
    reacting,
    composerRef,
    onClose,
    handleReact,
    handleComment,
    handleShare,
    handleCopyLink,
    handleOpenAdminOptions,
    handleSendComment,
    handleLoadMoreComments,
}) {
    const { t } = useTranslation("groupModals");
    const titleId = `post-modal-title-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;

    const image = post?.image || null;

    // The photo's ratio, read off a preload so the surface is only drawn once
    // its size is known.
    const [ratio, setRatio] = useState(null);
    useEffect(() => {
        setRatio(null);
        if (!image) return undefined;

        let cancelled = false;
        const probe = new Image();
        probe.onload = () => {
            if (!cancelled && probe.naturalWidth && probe.naturalHeight) {
                setRatio(probe.naturalWidth / probe.naturalHeight);
            }
        };
        probe.src = image;
        return () => {
            cancelled = true;
        };
    }, [image]);

    // Desktop sizes in px: column height from the viewport, photo width from
    // the ratio, capped so the side column always fits beside it.
    const [geometry, setGeometry] = useState(null);
    useLayoutEffect(() => {
        if (!open) {
            setGeometry(null);
            return undefined;
        }

        const compute = () => {
            if (window.innerWidth <= STACK_BELOW) {
                setGeometry(null);
                return;
            }
            const height = Math.round(Math.min(window.innerHeight * MAX_H, MAX_H_PX));
            const maxMediaWidth = Math.min(
                window.innerWidth * MAX_W - SIDE_WIDTH - GAP,
                MEDIA_MAX_WIDTH
            );
            const mediaWidth = ratio
                ? Math.max(0, Math.round(Math.min(height * ratio, maxMediaWidth)))
                : 0;
            setGeometry({ height, mediaWidth });
        };

        compute();
        window.addEventListener("resize", compute);
        return () => window.removeEventListener("resize", compute);
    }, [open, ratio]);

    if (!open) return null;

    const hasImage = !!image && !!ratio;
    const layoutStyle = geometry ? { height: geometry.height } : undefined;
    const mediaStyle = geometry
        ? { width: geometry.mediaWidth, height: geometry.height }
        : undefined;

    return createPortal(
        <div
            className="post-modal"
            style={{ zIndex }}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
        >
            {/* `MuiBackdrop-root` picks up the app's themed backdrop color. */}
            <div
                className="post-modal__backdrop MuiBackdrop-root"
                onClick={onClose}
                aria-hidden="true"
            />

            {notFound ? (
                <div className="post-modal__layout post-modal__layout--text">
                    <div className="post-modal__side">
                        <section className="post-modal__panel post-modal__missing">
                            <Text.h5 weight={500} contrast={9} id={titleId}>
                                {t("postModal.notFound")}
                            </Text.h5>
                            <Button
                                color="inverse"
                                size="small"
                                startIcon={<IconX />}
                                onClick={onClose}
                                preventLoading
                            >
                                {t("common:actions.close")}
                            </Button>
                        </section>
                    </div>
                </div>
            ) : (
                <div
                    className={`post-modal__layout${hasImage ? "" : " post-modal__layout--text"}`}
                    style={layoutStyle}
                >
                    {/* ── 01: the photo ───────────────────────────────── */}
                    {hasImage && (
                        <section className="post-modal__media" style={mediaStyle}>
                            <img src={image} alt="" />
                        </section>
                    )}

                    <div className="post-modal__side">
                        {/* ── 02.A: publisher, where, text, actions ────── */}
                        <PostInfo
                            post={post}
                            loading={loading}
                            isAdmin={isAdmin}
                            reacting={reacting}
                            titleId={titleId}
                            onClose={onClose}
                            onReact={handleReact}
                            onComment={handleComment}
                            onShare={handleShare}
                            onCopyLink={handleCopyLink}
                            onOpenAdminOptions={handleOpenAdminOptions}
                        />

                        {/* ── 02.B: the thread + composer ──────────────── */}
                        <Comments
                            comments={comments}
                            me={me}
                            ready={!loading}
                            sending={sending}
                            composerRef={composerRef}
                            onSend={handleSendComment}
                            onLoadMore={handleLoadMoreComments}
                        />
                    </div>
                </div>
            )}
        </div>,
        document.body
    );
}
